/**
 * Timezone-aware date utilities for Australian providers.
 *
 * All functions accept an optional `tz` parameter (IANA timezone string).
 * When omitted, defaults to Australia/Sydney for backward compatibility.
 *
 * Usage in components:
 *   const tz = useTimezone();
 *   const today = todayLocal(tz);
 *
 * The old `*Sydney` names are kept as backward-compatible aliases that
 * call the new functions with no timezone argument (defaulting to Sydney).
 */

import { DEFAULT_TIMEZONE } from './timezone';
import { dateOnlyInTz } from '@statura/utils';

// ─── Primary functions (timezone-parameterised) ──────────────

/** Returns today's date in YYYY-MM-DD format in the given timezone. */
export function todayLocal(tz: string = DEFAULT_TIMEZONE): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: tz });
}

/** Returns tomorrow's date in YYYY-MM-DD format in the given timezone. */
export function tomorrowLocal(tz: string = DEFAULT_TIMEZONE): string {
  // Derive today in the target timezone first, then add 1 day.
  // This avoids the UTC arithmetic bug where setDate(+1) on a UTC Date
  // can produce a date 2 days ahead in AEST during the UTC morning window.
  const todayStr = todayLocal(tz);
  return addDaysLocal(todayStr, 1, tz);
}

/** Formats a Date or ISO string as YYYY-MM-DD in the given timezone. */
export function toDateStringLocal(date: Date | string, tz: string = DEFAULT_TIMEZONE): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-CA', { timeZone: tz });
}

const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Calendar-day arithmetic runs on a UTC-anchored date key so the runtime clock
 * never moves the result. A date-only key is used as given; any timestamp input
 * is first resolved to its date in the governing zone `tz`.
 */
function dateKeyAnchor(dateStr: string, tz: string): Date {
  const key = DATE_KEY.test(dateStr) ? dateStr : toDateStringLocal(dateStr, tz);
  return new Date(`${key}T00:00:00Z`);
}

/** Adds N days to a date string (YYYY-MM-DD) and returns YYYY-MM-DD in the given timezone. */
export function addDaysLocal(dateStr: string, days: number, tz: string = DEFAULT_TIMEZONE): string {
  const d = dateKeyAnchor(dateStr, tz);
  d.setUTCDate(d.getUTCDate() + days);
  return dateOnlyInTz(d, 'UTC');
}

/**
 * Returns the previous calendar month relative to "today" in the given timezone,
 * as inclusive YYYY-MM-DD bounds plus a human label (e.g. "May 2026").
 *
 * Used by the SAH month-close cockpit: "close the month" always means the prior
 * whole calendar month in the tenant's timezone — NOT a rolling N-day window.
 * The last-day-of-month arithmetic via Date.UTC(year, monthIndex, 0) is
 * timezone-agnostic (a month's day count is fixed), so no UTC date-string bug.
 */
export function previousCalendarMonth(tz: string = DEFAULT_TIMEZONE): {
  startDate: string;
  endDate: string;
  label: string;
} {
  const ymd = todayLocal(tz); // 'YYYY-MM-DD'
  const year = Number(ymd.slice(0, 4));
  const month = Number(ymd.slice(5, 7)); // 1-12
  const prevYear = month === 1 ? year - 1 : year;
  const prevMonth = month === 1 ? 12 : month - 1; // 1-12
  const mm = String(prevMonth).padStart(2, '0');
  // Date.UTC(prevYear, prevMonth, 0) → day 0 of the month AFTER prevMonth = last day of prevMonth.
  const lastDay = new Date(Date.UTC(prevYear, prevMonth, 0)).getUTCDate();
  const label = new Date(Date.UTC(prevYear, prevMonth - 1, 1)).toLocaleDateString('en-AU', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return {
    startDate: `${prevYear}-${mm}-01`,
    endDate: `${prevYear}-${mm}-${String(lastDay).padStart(2, '0')}`,
    label,
  };
}

/**
 * Adds N business days (Mon-Fri) to a date string and returns YYYY-MM-DD in
 * the given timezone.
 *
 * HOLIDAY-BLIND: weekends only — public holidays count as business days here.
 * Do NOT use this for statutory/regulatory deadlines; those must go through
 * the SQL calculator `add_business_days_au` (public-holiday aware,
 * state-scoped), as `record_complaint_controlled` does for the ACQSC
 * acknowledgement deadline. Acceptable for internal task due dates where an
 * earlier-than-necessary date is harmless.
 */
export function addBusinessDaysLocal(
  dateStr: string,
  businessDays: number,
  tz: string = DEFAULT_TIMEZONE,
): string {
  const d = dateKeyAnchor(dateStr, tz);
  let added = 0;
  while (added < businessDays) {
    d.setUTCDate(d.getUTCDate() + 1);
    const day = d.getUTCDay();
    if (day !== 0 && day !== 6) added++;
  }
  return dateOnlyInTz(d, 'UTC');
}

/** Returns the number of business days between two YYYY-MM-DD date strings. */
export function businessDaysBetween(
  fromStr: string,
  toStr: string,
  tz: string = DEFAULT_TIMEZONE,
): number {
  const from = dateKeyAnchor(fromStr, tz);
  const to = dateKeyAnchor(toStr, tz);
  const sign = to >= from ? 1 : -1;
  const start = sign === 1 ? from : to;
  const end = sign === 1 ? to : from;
  let count = 0;
  const cursor = new Date(start);
  while (cursor < end) {
    cursor.setUTCDate(cursor.getUTCDate() + 1);
    const day = cursor.getUTCDay();
    if (day !== 0 && day !== 6) count++;
  }
  return count * sign;
}

/** Formats a Date or ISO string for display in en-AU locale. */
export function formatDateAU(
  date: Date | string,
  options?: Intl.DateTimeFormatOptions,
  tz: string = DEFAULT_TIMEZONE,
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-AU', { timeZone: tz, ...options });
}

/** Formats an instant for roster operations in an explicit tenant timezone. */
export function formatTenantDateTimeAU(instant: string, tz: string): string {
  const date = new Date(instant);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-AU', {
    timeZone: tz,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
}

/** Formats only the local wall-clock time in the tenant timezone. */
export function formatTimeAU(date: Date | string, tz: string = DEFAULT_TIMEZONE): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleTimeString('en-AU', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: tz,
  });
}

/**
 * Formats an ISO calendar date (`YYYY-MM-DD`) without interpreting it as an
 * instant. Calendar dates such as dates of birth, assignment dates and expiry
 * dates must not shift when the browser and tenant use different timezones.
 */
export function formatIsoDateOnlyAU(
  dateOnly: string,
  options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  },
): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateOnly.trim());
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(Date.UTC(year, month - 1, day));

  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    return null;
  }

  return parsed.toLocaleDateString('en-AU', { timeZone: 'UTC', ...options });
}

/** Formats a Date or ISO string as dd/mm/yyyy. */
export function formatDateDDMMYYYY(date: Date | string, tz: string = DEFAULT_TIMEZONE): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: tz,
  });
}

// ─── Day-boundary helpers (timezone-aware) ──────────────────

/**
 * Returns the UTC offset string (e.g. '+10:00', '+09:30', '-05:00')
 * that applies to a given IANA timezone AT a specific instant.
 *
 * Uses Intl.DateTimeFormat to derive the offset so it correctly
 * handles DST transitions. Pass `instant` to get the offset for a
 * specific date (e.g. a backdated clinical record): AEST/AEDT differ
 * by an hour, so a January date must use +11:00 even when "now" is
 * +10:00. Omit `instant` to get the offset for the current moment.
 */
export function getTimezoneOffset(
  tz: string = DEFAULT_TIMEZONE,
  instant: Date = new Date(),
): string {
  // Format with timeZoneName 'longOffset' gives e.g. "GMT+10:00"
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    timeZoneName: 'longOffset',
  }).formatToParts(instant);
  const offsetPart = parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  // offsetPart is like "GMT+10:00" or "GMT-05:00" or "GMT" (for UTC)
  const match = offsetPart.match(/GMT([+-]\d{2}:\d{2})/);
  return match?.[1] ?? '+00:00';
}

/**
 * Offset (e.g. "+10:00") in effect at a specific LOCAL wall-clock time on
 * `dateStr` in `tz`.
 *
 * Two-pass: a noon-UTC sample gives a stable seed offset (noon is never inside
 * the ~2-3am DST transition window), then we form the actual local instant with
 * that seed and re-sample. This resolves the correct offset even when the
 * requested wall-clock time sits on the OTHER side of a same-day DST transition
 * — which is exactly the case for the 00:00:00 and 23:59:59 bounds on a
 * transition day, where local midnight and local end-of-day are in different
 * offsets. One refinement converges because both bounds are far from 2-3am.
 */
function offsetForLocalWallClock(tz: string, dateStr: string, timeStr: string): string {
  const seed = getTimezoneOffset(tz, new Date(`${dateStr}T12:00:00Z`));
  return getTimezoneOffset(tz, new Date(`${dateStr}T${timeStr}${seed}`));
}

/**
 * Interprets a naive wall-clock datetime string ("YYYY-MM-DDTHH:mm" or
 * "YYYY-MM-DDTHH:mm:ss", with an optional trailing offset/Z which is ignored)
 * as a local time in `tz` and returns the corresponding UTC instant as an
 * ISO-8601 string.
 *
 * This is the correct replacement for `new Date(naive).toISOString()`, which
 * (mis)interprets the naive string in the RUNTIME's timezone — the browser for
 * client code, UTC for server code — instead of the tenant's timezone. For a
 * Perth tenant, `wallClockToInstant('2026-07-01T09:00', 'Australia/Perth')`
 * yields the instant for 09:00 AWST, not 09:00 in the browser's zone.
 *
 * DST-exact via offsetForLocalWallClock, so a wall-clock time on either side of
 * a same-day DST transition resolves its own offset. A non-existent spring-forward
 * gap time (e.g. 02:30 on a DST-start day) deterministically resolves to the
 * post-transition instant (standard convention); ambiguous fall-back times resolve
 * to one occurrence. Accepts an optional fractional-second suffix (dropped). A
 * genuinely non-datetime input falls to native parsing, which fails loud on
 * garbage rather than silently persisting a runtime-tz instant.
 */
export function wallClockToInstant(naive: string, tz: string = DEFAULT_TIMEZONE): string {
  const trimmed = naive.trim();
  // If the string already carries a zone designator (Z or ±HH[:]MM) it is an
  // instant, not a naive wall clock — pass it through unchanged so an
  // already-converted value can never be double-interpreted.
  if (/(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(trimmed)) return new Date(trimmed).toISOString();
  // Accept "YYYY-MM-DD(T| )HH:mm" with optional ":ss" and optional ".SSS" fraction
  // (fraction dropped). Without the fraction branch, a value like "...T09:00:00.000"
  // would miss the match and be parsed in the RUNTIME tz — the exact bug we fix.
  const match = trimmed.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/);
  if (!match) return new Date(trimmed).toISOString();
  const [, dateStr, hh, mm, ss] = match;
  if (!dateStr || !hh || !mm) return new Date(trimmed).toISOString();
  const timeStr = `${hh}:${mm}:${ss ?? '00'}`;
  const offset = offsetForLocalWallClock(tz, dateStr, timeStr);
  return new Date(`${dateStr}T${timeStr}${offset}`).toISOString();
}

/**
 * Current instant expressed as a naive wall-clock "YYYY-MM-DDTHH:mm" string in
 * `tz` — suitable for pre-filling an HTML datetime-local input so it shows the
 * tenant's current local time rather than the browser's. Pairs with
 * wallClockToInstant on submit.
 */
export function nowWallClockLocal(tz: string = DEFAULT_TIMEZONE): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date());
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  // Some engines emit hour '24' for midnight under hour12:false — normalise.
  const hour = get('hour') === '24' ? '00' : get('hour');
  return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}`;
}

/**
 * Returns start/end timestamps for a given date string (YYYY-MM-DD)
 * with the correct timezone offset appended.
 *
 * Usage:
 *   const { start, end } = dayBounds('2026-04-14', 'Australia/Sydney');
 *   // start = '2026-04-14T00:00:00+10:00'
 *   // end   = '2026-04-14T23:59:59+10:00'
 *
 * DST-exact: each bound resolves its OWN offset, so on a transition day the
 * start (local midnight) and end (local 23:59:59) can legitimately carry
 * different offsets — e.g. Sydney 2026-10-04 is
 * `2026-10-04T00:00:00+10:00` .. `2026-10-04T23:59:59+11:00`. A single
 * day-wide offset would put one bound an hour off and drop/duplicate boundary
 * rows (and corrupt the common half-open `dayBounds(next).start` pattern).
 */
export function dayBounds(
  dateStr: string,
  tz: string = DEFAULT_TIMEZONE,
): { start: string; end: string } {
  return {
    start: `${dateStr}T00:00:00${offsetForLocalWallClock(tz, dateStr, '00:00:00')}`,
    end: `${dateStr}T23:59:59${offsetForLocalWallClock(tz, dateStr, '23:59:59')}`,
  };
}

// ─── Backward-compatible aliases (default to Sydney) ─────────
// These allow all 53 existing importers to continue working unchanged.

/** @deprecated Use todayLocal(tz) instead */
export const todaySydney = () => todayLocal();

/** @deprecated Use tomorrowLocal(tz) instead */
export const tomorrowSydney = () => tomorrowLocal();

/** @deprecated Use toDateStringLocal(date, tz) instead */
export const toDateStringSydney = (date: Date | string) => toDateStringLocal(date);

/** @deprecated Use addDaysLocal(dateStr, days, tz) instead */
export const addDaysSydney = (dateStr: string, days: number) => addDaysLocal(dateStr, days);

/** @deprecated Use addBusinessDaysLocal(dateStr, days, tz) instead */
export const addBusinessDaysSydney = (dateStr: string, businessDays: number) =>
  addBusinessDaysLocal(dateStr, businessDays);
